// Datos del Dashboard IA: lee las mismas detecciones que la pestaña IA (pirIaAnalyzes vía backend),
// las normaliza y calcula todas las métricas del panel.
import api from '../../../api/nodeBackApi';
import { aplicarZonasFoto, getZonasSeguro } from './zonas';

// ---------------------------------------------------------------------------
// Clases y familias (mismo color en barras, chips, cajas y recortes)
// ---------------------------------------------------------------------------
export const FAMILIES = [
  { key: 'personas', label: 'Personas', classes: ['Persona con casco', 'Persona sin casco'] },
  { key: 'vehiculos', label: 'Vehículos', classes: ['Automovil', 'Camioneta'] },
  { key: 'maquinaria', label: 'Maquinaria y camiones', classes: ['Excavadora', 'Retroexcavadora', 'Camion volquete', 'Camion', 'Mixer'] },
];

export const CLASS_INFO = {
  'Persona con casco': { label: 'Persona con casco', color: '#10b981', family: 'personas' },
  'Persona sin casco': { label: 'Persona sin casco', color: '#ef4444', family: 'personas' },
  Automovil: { label: 'Auto', color: '#3b82f6', family: 'vehiculos' },
  Camioneta: { label: 'Camioneta', color: '#6366f1', family: 'vehiculos' },
  Excavadora: { label: 'Excavadora', color: '#f59e0b', family: 'maquinaria' },
  Retroexcavadora: { label: 'Retroexcavadora', color: '#d97706', family: 'maquinaria' },
  'Camion volquete': { label: 'Camión volquete', color: '#f97316', family: 'maquinaria' },
  Camion: { label: 'Camión', color: '#c2410c', family: 'maquinaria' },
  Mixer: { label: 'Mixer', color: '#a855f7', family: 'maquinaria' },
};

export const ALL_CLASSES = Object.keys(CLASS_INFO);
const PERSON = new Set(['Persona con casco', 'Persona sin casco']);
const MACHINE = new Set(FAMILIES[2].classes);

export const classInfo = (c) => CLASS_INFO[c] || { label: c, color: '#9ca3af', family: 'otros' };
export const isPerson = (c) => PERSON.has(c);
export const isMachine = (c) => MACHINE.has(c);

// ---------------------------------------------------------------------------
// Tiempo: todo se muestra en hora de obra (Argentina, UTC-3)
// ---------------------------------------------------------------------------
const AR_OFF = 3 * 3600e3;
export const ar = (ms) => new Date(ms - AR_OFF); // usar getUTC* sobre esto
// Versiones con aritmética pura (se llaman decenas de miles de veces: crear fechas era el cuello de botella)
const dayKeyCache = new Map();
export const dayKey = (ms) => {
  const n = Math.floor((ms - AR_OFF) / 864e5);
  let k = dayKeyCache.get(n);
  if (!k) { k = new Date(n * 864e5).toISOString().slice(0, 10); dayKeyCache.set(n, k); }
  return k;
};
export const arMin = (ms) => Math.floor((((ms - AR_OFF) % 864e5) + 864e5) % 864e5 / 60000);
export const arHour = (ms) => Math.floor(arMin(ms) / 60);
export const dayStartMs = (key) => Date.parse(key + 'T00:00:00Z') + AR_OFF;
export const addDays = (key, n) => new Date(Date.parse(key + 'T00:00:00Z') + n * 864e5).toISOString().slice(0, 10);
export const todayKey = () => dayKey(Date.now());
export const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 864e5);
export const listDays = (from, to) => {
  const out = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
};

const pad = (n) => String(n).padStart(2, '0');
export const fmtHM = (ms) => { const d = ar(ms); return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`; };
export const fmtDM = (ms) => { const d = ar(ms); return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}`; };
export const fmtDMY = (ms) => { const d = ar(ms); return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${String(d.getUTCFullYear()).slice(2)}`; };
export const fmtKey = (key) => `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(2, 4)}`;
const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
export const fmtDow = (key) => DOW[new Date(key + 'T12:00:00Z').getUTCDay()];

// ---------------------------------------------------------------------------
// Lectura: mismo endpoint que usa la pestaña IA, un día por llamada, con caché
// ---------------------------------------------------------------------------
const dayCache = new Map(); // `${cam}|${customer}|${day}` -> Promise<doc[]>

function normalize(raw) {
  const ms = raw.date ? Date.parse(raw.date) : (raw.timestamp?._seconds || 0) * 1000;
  const key = String(raw.s3_key || '');
  const m = key.match(/^s3:\/\/([^/]+)\/(.+)$/);
  let dets;
  const hasBoxes = Array.isArray(raw.detections);
  if (hasBoxes) {
    dets = raw.detections.map((d) => ({
      cls: d.class_name,
      conf: typeof d.confidence === 'number' ? d.confidence : 1,
      x1: d.bbox?.x1, y1: d.bbox?.y1, x2: d.bbox?.x2, y2: d.bbox?.y2,
    }));
  } else {
    // Fotos anteriores al 30/05/2026: solo hay conteos, sin cajas
    const rep = (cls, n) => Array.from({ length: Math.max(0, n | 0) }, () => ({ cls, conf: 1 }));
    const p = raw.numberOfPerson | 0, h = Math.min(p, raw.numberOfPersonWithHelmet | 0);
    dets = [
      ...rep('Persona con casco', h), ...rep('Persona sin casco', p - h),
      ...rep('Automovil', raw.numberOfCars), ...rep('Camioneta', raw.numberOfPickUps), ...rep('Excavadora', raw.numberOfExcavators),
    ];
  }
  return {
    id: key || String(ms),
    ms,
    s3: m ? m[2] : key,
    bucket: m ? m[1] : '',
    w: raw.image_width || 1920,
    h: raw.image_height || 1080,
    hasBoxes,
    dets,
  };
}

// Caché persistente en el navegador (IndexedDB): un día que ya pasó no cambia más,
// así que la segunda vez que se mira un período carga al instante.
const IDB_NAME = 'lisual-ia-dash', IDB_STORE = 'dias', IDB_VER = 2; // v2: se descarta la caché vieja (guardaba días vacíos para siempre)
let idbP = null;
const idb = () => {
  if (!idbP) {
    idbP = new Promise((res) => {
      try {
        const rq = indexedDB.open(IDB_NAME, IDB_VER);
        rq.onupgradeneeded = () => { const db = rq.result; if (db.objectStoreNames.contains(IDB_STORE)) db.deleteObjectStore(IDB_STORE); db.createObjectStore(IDB_STORE); };
        rq.onsuccess = () => res(rq.result);
        rq.onerror = () => res(null);
        rq.onblocked = () => res(null); // otra pestaña con la versión vieja abierta: se sigue sin caché
      } catch (e) { res(null); }
    });
  }
  return idbP;
};
const idbGet = async (k) => {
  const db = await idb();
  if (!db) return undefined;
  return new Promise((res) => {
    try { const r = db.transaction(IDB_STORE).objectStore(IDB_STORE).get(k); r.onsuccess = () => res(r.result); r.onerror = () => res(undefined); } catch (e) { res(undefined); }
  });
};
const idbPut = async (k, v) => {
  const db = await idb();
  if (!db) return;
  try { db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(v, k); } catch (e) { /* cuota llena: se ignora */ }
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Un día: primero la caché local; si no está, el backend con tiempo máximo y reintentos
// (antes un pedido colgado dejaba toda la carga esperando para siempre).
// La caché persistente solo guarda días "cerrados de verdad": la IA de las fotos va con horas de atraso
// (cron_s3 mira hasta 24 h para atrás) y a veces se cargan días viejos después (cámara a la que se le
// activó la IA más tarde). Antes se guardaba para siempre lo que hubiera a partir del día anterior a ayer,
// incluso un día vacío o a medio procesar, y esa fecha pasada ya no volvía a cargar nunca.
// Ahora: no se guardan días vacíos y lo guardado se vuelve a pedir si se leyó antes de 3 días de cerrado.
const SETTLE_MS = 3 * 864e5;
function fetchDay(cam, customerId, key) {
  const ck = `${cam}|${customerId}|${key}`;
  if (!dayCache.has(ck)) {
    const closed = key < addDays(todayKey(), -1); // hoy y ayer pueden seguir sumando fotos
    const dayEnd = dayStartMs(addDays(key, 1));
    const p = (async () => {
      if (closed) {
        const hit = await idbGet(ck);
        if (hit && Array.isArray(hit.docs) && hit.docs.length && hit.at - dayEnd >= SETTLE_MS) return hit.docs;
      }
      const [y, mo, d] = key.split('-');
      let lastErr;
      for (let i = 0; i < 4; i++) {
        try {
          const r = await api.post(`/api/dataAnalyzed/getDailyData/${encodeURIComponent(cam)}/`, { day: d, month: mo, year: Number(y), customerId }, { timeout: 12000 });
          const docs = Array.isArray(r.data) ? r.data.map(normalize) : [];
          if (closed && docs.length) idbPut(ck, { at: Date.now(), docs });
          return docs;
        } catch (e) { lastErr = e; await sleep(400 * (i + 1)); }
      }
      throw lastErr;
    })().catch((e) => { dayCache.delete(ck); throw e; });
    dayCache.set(ck, p);
  }
  return dayCache.get(ck);
}

const finish = (out, zonas) => {
  // Copia propia: recharts congela los objetos que recibe y markStatic necesita escribir.
  // Las zonas de detección se aplican acá, así todo lo que sale de iaData (gráficos, indicadores,
  // mapa de calor, visor, calendario, vehículos) ya viene sin lo que el cliente pidió ignorar.
  const photos = [...out.values()].map((p) => { const z = aplicarZonasFoto(p, zonas); return { ...z, dets: z.dets.map((d) => ({ ...d })) }; }).sort((a, b) => a.ms - b.ms);
  markStatic(photos);
  return photos;
};

// Trae los días pedidos (10 en paralelo, los más recientes primero) y devuelve las fotos ordenadas.
// onProgress(hechos, total, fotosHastaAhora) permite ir mostrando el panel mientras carga.
// opts.zonas: zonas de detección ya leídas; opts.feedId: las lee del backend (con caché).
// opts.sinZonas: devuelve todas las detecciones (lo usa el editor de zonas para la vista previa).
export async function loadDays(cam, customerId, days, onProgress, opts = {}) {
  const zonas = opts.sinZonas ? null : opts.zonas !== undefined ? opts.zonas : opts.feedId ? await getZonasSeguro(opts.feedId) : null;
  const out = new Map();
  let done = 0, lastPaint = 0;
  const queue = [...days].sort().reverse();
  const worker = async () => {
    while (queue.length) {
      const k = queue.shift();
      try {
        (await fetchDay(cam, customerId, k)).forEach((doc) => out.set(doc.id, doc));
      } catch (e) { /* día con error tras 4 intentos: queda afuera */ }
      done += 1;
      if (onProgress) {
        const now = Date.now();
        const paint = done === days.length || now - lastPaint > 2500;
        if (paint) lastPaint = now;
        onProgress(done, days.length, paint && done < days.length ? finish(out, zonas) : null);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(10, days.length) }, worker));
  return finish(out, zonas);
}

// ---------------------------------------------------------------------------
// Objetos quietos: misma clase en el mismo lugar que en la foto anterior (≤ 40 min)
// = máquina estacionada, poste o cartel. No cuentan como actividad.
// ---------------------------------------------------------------------------
const iou = (a, b) => {
  const ix = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
  const iy = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
  const inter = ix * iy;
  const u = (a.x2 - a.x1) * (a.y2 - a.y1) + (b.x2 - b.x1) * (b.y2 - b.y1) - inter;
  return u > 0 ? inter / u : 0;
};
function markStatic(photos) {
  let prev = null;
  for (const p of photos) {
    for (const d of p.dets) {
      d.static = false;
      if (!p.hasBoxes || !prev || !prev.hasBoxes || p.ms - prev.ms > 40 * 60e3) continue;
      const fam = classInfo(d.cls).family;
      d.static = prev.dets.some((o) => classInfo(o.cls).family === fam && iou(d, o) > 0.7);
    }
    prev = p;
  }
}

// ---------------------------------------------------------------------------
// Filtros (clases activas + confianza mínima por clase + horario de obra)
// ---------------------------------------------------------------------------
export const DEFAULT_CFG = {
  enabled: ALL_CLASSES,
  thr: Object.fromEntries(ALL_CLASSES.map((c) => [c, 0])),
  workFrom: 7,
  workTo: 19,
  onlyWork: true,
  blurPeople: false,
  jIni: 8 * 60, // jornada pactada (minutos desde las 0 h) para medir arranque y cierre
  jFin: 17 * 60,
};

// ---------------------------------------------------------------------------
// Correcciones de casco hechas por el cliente. El modelo marca "sin casco" a mucha gente
// que sí lo tiene (agachados, cascos azules/verdes/rojos). Con lo que el cliente revisa se
// calcula la tasa de falsas alarmas y se corrige el % de casco del resto.
// ---------------------------------------------------------------------------
export const detKey = (photo, d) => `${photo.id}|${Math.round(d.x1)},${Math.round(d.y1)}`;
const HELMET = { cam: null, fixes: new Map(), rate: null, reviewed: 0, falsas: 0 };
const recalcRate = () => {
  let con = 0, sin = 0;
  for (const f of HELMET.fixes.values()) if (f.orig === 'Persona sin casco') { if (f.val === 'con') con += 1; else if (f.val === 'sin') sin += 1; }
  HELMET.reviewed = con + sin; HELMET.falsas = con;
  // Suavizado (Laplace): con pocas revisiones nunca da 0% o 100% de falsas alarmas
  HELMET.rate = con + sin >= 8 ? (con + 1) / (con + sin + 2) : null;
};
export function loadHelmetFixes(cam) {
  if (HELMET.cam !== cam) {
    HELMET.cam = cam;
    try { HELMET.fixes = new Map(Object.entries(JSON.parse(localStorage.getItem('iaDash:fix:' + cam) || '{}'))); } catch (e) { HELMET.fixes = new Map(); }
    recalcRate();
  }
  return HELMET;
}
export function setFix(photo, d, val) {
  const k = detKey(photo, d);
  if (val == null) HELMET.fixes.delete(k); else HELMET.fixes.set(k, { orig: d.cls, val, ms: photo.ms, s3: photo.s3, box: [d.x1, d.y1, d.x2, d.y2] });
  try { localStorage.setItem('iaDash:fix:' + HELMET.cam, JSON.stringify(Object.fromEntries(HELMET.fixes))); } catch (e) { /* sin storage */ }
  recalcRate();
}
export const getFix = (photo, d) => HELMET.fixes.get(detKey(photo, d));
export const helmetInfo = () => ({ rate: HELMET.rate, reviewed: HELMET.reviewed, falsas: HELMET.falsas, total: HELMET.fixes.size });
export const exportFixes = () => [...HELMET.fixes.values()];

// Detecciones visibles con las correcciones del cliente aplicadas (fix: 'con' | 'sin' | 'no' = no es persona)
export const visibleDets = (photo, cfg) => {
  const out = [];
  for (const d of photo.dets) {
    if (!cfg.enabled.includes(d.cls) || d.conf < (cfg.thr[d.cls] ?? 0)) continue;
    const f = HELMET.fixes.size && d.x1 != null ? HELMET.fixes.get(detKey(photo, d)) : null;
    if (f && f.val === 'no') continue;
    out.push(f ? { ...d, cls: f.val === 'con' ? 'Persona con casco' : 'Persona sin casco', fixed: true, orig: d.cls } : d);
  }
  return out;
};

export const inWork = (ms, cfg) => {
  if (!cfg.onlyWork) return true;
  const h = arHour(ms);
  return h >= cfg.workFrom && h < cfg.workTo;
};

// Métricas de una foto (memorizadas por foto y configuración: se piden miles de veces)
const statsCache = new WeakMap();
export function photoStats(photo, cfg) {
  const c = statsCache.get(photo);
  if (c && c.cfg === cfg) return c.s;
  const s = computeStats(photo, cfg);
  statsCache.set(photo, { cfg, s });
  return s;
}
function computeStats(photo, cfg) {
  const dets = visibleDets(photo, cfg);
  const counts = {};
  let persons = 0, helmet = 0, helmetIA = 0, activity = 0, machines = 0;
  for (const d of dets) {
    counts[d.cls] = (counts[d.cls] || 0) + 1;
    if (isPerson(d.cls)) {
      persons += 1;
      if (!d.static) activity += 1;
      if (d.cls === 'Persona con casco') { helmet += 1; if (!d.fixed) helmetIA += 1; }
      else if (!d.fixed && HELMET.rate != null) helmet += HELMET.rate; // "sin casco" sin revisar: se corrige con la tasa de falsas alarmas
      if (d.fixed && d.orig === 'Persona con casco') helmetIA += 1;
    }
    if (isMachine(d.cls)) { machines += 1; if (!d.static) activity += 2; }
  }
  return { dets, counts, persons, helmet, helmetIA, machines, activity, total: dets.length };
}

// ---------------------------------------------------------------------------
// Agregación por intervalos (foto / hora / día / semana / mes)
// ---------------------------------------------------------------------------
export const GRANS = [
  { key: 'foto', label: 'Foto', ms: 0 },
  { key: 'hora', label: 'Hora', ms: 3600e3 },
  { key: 'dia', label: 'Día', ms: 864e5 },
  { key: 'semana', label: 'Semana', ms: 7 * 864e5 },
  { key: 'mes', label: 'Mes', ms: 30 * 864e5 },
];

const bucketStart = (ms, gran) => {
  if (gran === 'foto') return ms;
  if (gran === 'hora') return Math.floor(ms / 3600e3) * 3600e3; // AR es UTC-3 justo: las horas coinciden
  const n = Math.floor((ms - AR_OFF) / 864e5); // número de día en hora de obra
  if (gran === 'dia') return n * 864e5 + AR_OFF;
  if (gran === 'semana') return (n - ((n + 3) % 7)) * 864e5 + AR_OFF; // el día 0 (1/1/1970) fue jueves → lunes = (n+3)%7==0
  const d = new Date(n * 864e5);
  d.setUTCDate(1);
  return d.getTime() + AR_OFF;
};

export const bucketEnd = (start, gran) => {
  if (gran === 'mes') { const d = ar(start); d.setUTCMonth(d.getUTCMonth() + 1); return d.getTime() + AR_OFF; }
  return start + (GRANS.find((g) => g.key === gran)?.ms || 1);
};

// Granularidades posibles según la cantidad de datos (como Enlaps: no más de ~120 barras)
export function allowedGrans(photos, fromMs, toMs) {
  const days = (toMs - fromMs) / 864e5;
  const ok = { foto: photos.length > 0 && photos.length <= 150, hora: days <= 14, dia: days >= 1 && days <= 200, semana: days >= 14, mes: days >= 60 };
  return GRANS.filter((g) => ok[g.key]).map((g) => g.key);
}
export function autoGran(photos, fromMs, toMs) {
  const span = toMs - fromMs;
  if (photos.length && photos.length <= 60) return 'foto';
  if (span <= 2 * 864e5) return 'hora';
  if (span <= 120 * 864e5) return 'dia';
  if (span <= 400 * 864e5) return 'semana';
  return 'mes';
}

export function aggregate(photos, cfg, gran) {
  const map = new Map();
  for (const p of photos) {
    const s = photoStats(p, cfg);
    const k = bucketStart(p.ms, gran);
    let b = map.get(k);
    if (!b) { b = { start: k, n: 0, sums: {}, persons: 0, helmet: 0, activity: 0, peak: 0 }; map.set(k, b); }
    b.n += 1;
    for (const [c, v] of Object.entries(s.counts)) b.sums[c] = (b.sums[c] || 0) + v;
    b.persons += s.persons; b.helmet += s.helmet; b.activity += s.activity;
    b.peak = Math.max(b.peak, s.persons);
  }
  return [...map.values()].sort((a, b) => a.start - b.start).map((b) => {
    const row = {
      start: b.start, end: gran === 'foto' ? b.start : bucketEnd(b.start, gran), n: b.n,
      actividad: +(b.activity / b.n).toFixed(2),
      casco: b.persons ? Math.min(100, Math.round((100 * b.helmet) / b.persons)) : null,
      pico: b.peak,
    };
    for (const c of ALL_CLASSES) row[c] = +((b.sums[c] || 0) / b.n).toFixed(3);
    return row;
  });
}

// Completa los intervalos del período que no tienen fotos analizadas (cámara sin IA esos días, sin señal, feriado)
// con filas vacías: así el gráfico muestra el período entero y el clima de esos días también se ve.
// En vista por hora y con jornada de obra, solo se completan las horas de la jornada.
export function fillGaps(rows, gran, fromMs, toMs, cfg) {
  if (gran === 'foto' || toMs <= fromMs) return rows;
  const have = new Map(rows.map((r) => [r.start, r]));
  const out = [];
  let guard = 0;
  for (let k = bucketStart(fromMs, gran); k < toMs && guard < 5000; k = bucketEnd(k, gran), guard += 1) {
    const r = have.get(k);
    if (r) { out.push(r); continue; }
    if (gran === 'hora' && !inWork(k, cfg)) continue;
    const row = { start: k, end: bucketEnd(k, gran), n: 0, actividad: null, casco: null, pico: 0, empty: true };
    for (const c of ALL_CLASSES) row[c] = 0;
    out.push(row);
  }
  // alguna fila con fotos que no cayó en la grilla (no debería pasar) no se pierde
  const used = new Set(out.map((r) => r.start));
  const extra = rows.filter((r) => !used.has(r.start));
  return extra.length ? [...out, ...extra].sort((a, b) => a.start - b.start) : out;
}

export const bucketLabel = (ms, gran) => {
  if (gran === 'foto' || gran === 'hora') return `${fmtDM(ms)} ${fmtHM(ms)}`;
  if (gran === 'mes') { const d = ar(ms); return `${['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`; }
  return fmtDMY(ms);
};

// ---------------------------------------------------------------------------
// Indicadores de un período (lo que le sirve al jefe de obra)
// ---------------------------------------------------------------------------
export function periodKpis(photos, cfg) {
  const ph = photos.filter((p) => inWork(p.ms, cfg));
  let act = 0, persons = 0, helmet = 0, helmetIA = 0, hh = 0, peak = 0, machineH = 0;
  const days = new Map();
  ph.forEach((p, i) => {
    const s = photoStats(p, cfg);
    const next = ph[i + 1];
    const dt = Math.min(next && dayKey(next.ms) === dayKey(p.ms) ? next.ms - p.ms : 10 * 60e3, 15 * 60e3) / 3600e3;
    act += s.activity; persons += s.persons; helmet += s.helmet; helmetIA += s.helmetIA;
    hh += s.persons * dt;
    machineH += s.dets.filter((d) => isMachine(d.cls) && !d.static).length * dt;
    peak = Math.max(peak, s.persons);
    const k = dayKey(p.ms);
    const d = days.get(k) || { first: null, last: null, hh: 0, peak: 0, persons: 0, helmet: 0, n: 0 };
    d.n += 1; d.hh += s.persons * dt; d.peak = Math.max(d.peak, s.persons); d.persons += s.persons; d.helmet += s.helmet;
    if (s.activity > 0) { if (d.first == null) d.first = p.ms; d.last = p.ms; }
    days.set(k, d);
  });
  const active = [...days.values()].filter((d) => d.first != null);
  const avgMin = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const minOfDay = (ms) => { const d = ar(ms); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
  return {
    fotos: ph.length,
    actividad: ph.length ? act / ph.length : null,
    casco: persons ? Math.min(100, (100 * helmet) / persons) : null,
    cascoIA: persons ? (100 * helmetIA) / persons : null,
    horasHombre: hh,
    horasMaquina: machineH,
    pico: peak,
    diasConFotos: days.size,
    diasActivos: active.length,
    inicio: avgMin(active.map((d) => minOfDay(d.first))),
    fin: avgMin(active.map((d) => minOfDay(d.last))),
    porDia: days,
  };
}

export const fmtMin = (m) => (m == null ? '—' : `${pad(Math.floor(m / 60))}:${pad(Math.round(m % 60))}`);

// Variación contra el período anterior (bien calculada: sobre N-1)
export const delta = (n, n1) => {
  if (n == null || n1 == null) return null;
  if (n1 === 0) return n === 0 ? 0 : null;
  return ((n - n1) / Math.abs(n1)) * 100;
};

// ---------------------------------------------------------------------------
// Imágenes: URL firmada por el backend en el tamaño justo (miniaturas de 320/640/1280 px, ver fotos.js).
// photoUrl(photo) sin ancho = la original 4K, como antes; photoUrl(photo, { w: 1280 }) = la miniatura.
// ---------------------------------------------------------------------------
export { photoUrl } from './fotos';
